import type { CSSProperties } from 'react';
import {
  Loader2,
  FileText,
  File,
  Globe,
  Presentation,
  PlayCircle,
  MessageCircle,
  Home,
  Settings,
  User,
  Search,
  Plus,
  Minus,
  Check,
  X,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Download,
  Upload,
  Trash2,
  Edit,
  Copy,
  Share,
  Heart,
  Star,
  Info,
  AlertCircle,
  AlertTriangle,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';
import type { SDUIComponentProps } from '../../renderer/ComponentRenderer';
import { cn } from '@/lib/utils';

// Map of icon names to Lucide components
const iconMap: Record<string, LucideIcon> = {
  'file-text': FileText,
  file: File,
  globe: Globe,
  presentation: Presentation,
  'play-circle': PlayCircle,
  'message-circle': MessageCircle,
  home: Home,
  settings: Settings,
  user: User,
  search: Search,
  plus: Plus,
  minus: Minus,
  check: Check,
  x: X,
  'chevron-right': ChevronRight,
  'chevron-left': ChevronLeft,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'external-link': ExternalLink,
  download: Download,
  upload: Upload,
  trash: Trash2,
  edit: Edit,
  copy: Copy,
  share: Share,
  heart: Heart,
  star: Star,
  info: Info,
  'alert-circle': AlertCircle,
  'alert-triangle': AlertTriangle,
  'help-circle': HelpCircle,
};

/**
 * Icon component - renders Lucide icons by name.
 */
export function Icon({ id, props, style }: SDUIComponentProps) {
  const {
    name,
    size = 'md',
    color = 'default',
    className,
  } = props as {
    name: string;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    color?: 'default' | 'primary' | 'secondary' | 'muted' | 'success' | 'warning' | 'error';
    className?: string;
  };

  const IconComponent = iconMap[name];

  if (!IconComponent) {
    return (
      <span id={id} className="text-muted-foreground text-xs" style={style as CSSProperties}>
        [icon: {name}]
      </span>
    );
  }

  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
    xl: 'h-8 w-8',
    '2xl': 'h-12 w-12',
  };

  const colorClasses = {
    default: 'text-foreground',
    primary: 'text-primary',
    secondary: 'text-secondary',
    muted: 'text-muted-foreground',
    success: 'text-green-500',
    warning: 'text-yellow-500',
    error: 'text-red-500',
  };

  return (
    <IconComponent
      id={id}
      className={cn(sizeClasses[size], colorClasses[color], className)}
      style={style as CSSProperties}
    />
  );
}

/**
 * Image component - displays an image with optional styling.
 */
export function Image({ id, props, style }: SDUIComponentProps) {
  const {
    src,
    alt = '',
    width,
    height,
    rounded = false,
    className,
  } = props as {
    src: string;
    alt?: string;
    width?: number | string;
    height?: number | string;
    rounded?: boolean | 'sm' | 'md' | 'lg' | 'full';
    className?: string;
  };

  const roundedClasses = {
    false: '',
    true: 'rounded-md',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full',
  };

  return (
    <img
      id={id}
      src={src}
      alt={alt}
      width={width}
      height={height}
      className={cn(
        roundedClasses[String(rounded) as keyof typeof roundedClasses] || '',
        className,
      )}
      style={style as CSSProperties}
    />
  );
}

/**
 * Table component - displays tabular data.
 */
export function Table({ id, props, style }: SDUIComponentProps) {
  const {
    columns = [],
    data = [],
    striped = true,
    className,
  } = props as {
    columns: Array<{ key: string; label: string; width?: string }>;
    data: Array<Record<string, unknown>>;
    striped?: boolean;
    className?: string;
  };

  return (
    <div id={id} className={cn('overflow-x-auto', className)} style={style as CSSProperties}>
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-muted">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                style={{ width: col.width }}
                className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-card divide-y divide-border">
          {data.map((row, rowIndex) => (
            <tr key={rowIndex} className={striped && rowIndex % 2 === 1 ? 'bg-muted/50' : ''}>
              {columns.map((col) => (
                <td key={col.key} className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                  {String(row[col.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Loading component - displays a loading spinner.
 */
export function Loading({ id, props, style }: SDUIComponentProps) {
  const {
    size = 'md',
    text,
    className,
  } = props as {
    size?: 'sm' | 'md' | 'lg';
    text?: string;
    className?: string;
  };

  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
  };

  return (
    <div
      id={id}
      className={cn('flex flex-col items-center justify-center', className)}
      style={style as CSSProperties}
    >
      <Loader2 className={cn('animate-spin text-primary', sizeClasses[size])} />
      {text && <p className="mt-2 text-sm text-muted-foreground">{text}</p>}
    </div>
  );
}

/**
 * Skeleton component - placeholder loading animation.
 */
export function Skeleton({ id, props, style }: SDUIComponentProps) {
  const {
    width = 'full',
    height = 'md',
    rounded = 'md',
    className,
  } = props as {
    width?: 'full' | 'half' | 'third' | string;
    height?: 'sm' | 'md' | 'lg' | string;
    rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
    className?: string;
  };

  const widthClasses: Record<string, string> = {
    full: 'w-full',
    half: 'w-1/2',
    third: 'w-1/3',
  };

  const heightClasses: Record<string, string> = {
    sm: 'h-4',
    md: 'h-8',
    lg: 'h-12',
  };

  const roundedClasses = {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full',
  };

  return (
    <div
      id={id}
      className={cn(
        'animate-pulse bg-muted',
        widthClasses[width] || width,
        heightClasses[height] || height,
        roundedClasses[rounded],
        className,
      )}
      style={style as CSSProperties}
    />
  );
}

/**
 * EmptyState component - displays a message when there's no content.
 */
export function EmptyState({ id, props, style }: SDUIComponentProps) {
  const { title, description, icon, className } = props as {
    title: string;
    description?: string;
    icon?: string;
    className?: string;
  };

  return (
    <div id={id} className={cn('text-center py-12', className)} style={style as CSSProperties}>
      {icon && (
        <div className="mb-4 text-muted-foreground">
          <span className="text-4xl">{icon}</span>
        </div>
      )}
      <h3 className="text-lg font-medium text-foreground">{title}</h3>
      {description && <p className="mt-2 text-sm text-muted-foreground">{description}</p>}
    </div>
  );
}
